import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    deleteDoc,
    doc,
    serverTimestamp,
    updateDoc,
    getDocs,
    setDoc,
    limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { getUserWorkspace } from './firestore';

// ── Multi-tenant workspace ──
let _wsId = null;
export const setOvertimeWorkspace = async (userId) => {
    if (!userId) return;
    _wsId = await getUserWorkspace(userId);
    return _wsId;
};
const withWs = (data) => ({ ...data, workspaceId: _wsId || 'default' });
const wsWhere = () => _wsId ? where('workspaceId', '==', _wsId) : null;
const wsQuery = (colRef, ...constraints) => {
    const ws = wsWhere();
    return ws ? query(colRef, ws, ...constraints) : query(colRef, ...constraints);
};

export const subscribeToUsers = (callback) => {
    const q = query(collection(db, 'users'));
    return onSnapshot(q, (snapshot) => {
        const users = [];
        snapshot.forEach((doc) => {
            users.push({ id: doc.id, ...doc.data() });
        });
        callback(users);
    });
};

export const addOvertimeRecord = async (record) => {
    try {
        await addDoc(collection(db, 'overtime_records'), withWs({
            ...record,
            createdAt: serverTimestamp()
        }));
        return { success: true };
    } catch (error) {
        console.error("Error adding overtime record: ", error);
        throw error;
    }
};

export const subscribeToOvertimeRecords = (callback, filters = {}) => {
    let q = collection(db, 'overtime_records');

    // Build query based on filters if needed
    // For now, let's just order by date descending
    // Real-world usage might filter by month/year here to avoid loading everything

    q = wsQuery(q, orderBy('date', 'desc'), orderBy('createdAt', 'desc'), limit(500));

    return onSnapshot(q, (snapshot) => {
        const records = [];
        snapshot.forEach((doc) => {
            records.push({ id: doc.id, ...doc.data() });
        });
        callback(records);
    });
};

export const updateOvertimeRecord = async (recordId, updates) => {
    try {
        await updateDoc(doc(db, 'overtime_records', recordId), { ...updates, updatedAt: serverTimestamp() });
        return { success: true };
    } catch (error) {
        console.error("Error updating overtime record: ", error);
        throw error;
    }
};

export const deleteOvertimeRecord = async (recordId) => {
    try {
        await deleteDoc(doc(db, 'overtime_records', recordId));
        return { success: true };
    } catch (error) {
        console.error("Error deleting overtime record: ", error);
        throw error;
    }
};

// Rate & Salary Management
export const saveUserRate = async (userId, rate) => {
    try {
        const settingsRef = doc(db, 'overtime_settings', userId);
        const snap = await getDocs(query(collection(db, 'overtime_settings'), where('userId', '==', userId)));
        const existing = snap.empty ? {} : snap.docs[0].data();
        await setDoc(settingsRef, withWs({
            ...existing,
            userId,
            hourlyRate: parseFloat(rate),
            updatedAt: serverTimestamp()
        }), { merge: true });
        return { success: true };
    } catch (error) {
        console.error("Error saving user rate: ", error);
        throw error;
    }
};

// ── Ajustes globales del workspace (refrigerio por defecto, sueldo mínimo) ──
// Un solo documento por workspace. Ojo: depende de que setOvertimeWorkspace
// ya haya resuelto; suscríbete sólo cuando el workspace esté listo.
const globalRef = () => doc(db, 'overtime_global', _wsId || 'default');

export const subscribeToGlobalSettings = (callback) => {
    return onSnapshot(globalRef(), (snap) => {
        callback(snap.exists() ? snap.data() : {});
    }, (error) => {
        console.error('Error subscribing to global overtime settings:', error);
        callback({});
    });
};

export const saveGlobalSettings = async (settings) => {
    try {
        await setDoc(globalRef(), withWs({ ...settings, updatedAt: serverTimestamp() }), { merge: true });
        return { success: true };
    } catch (error) {
        console.error("Error saving global overtime settings: ", error);
        throw error;
    }
};

export const saveEmployeeSettings = async (userId, settings) => {
    try {
        await setDoc(doc(db, 'overtime_settings', userId), withWs({
            userId,
            ...settings,
            updatedAt: serverTimestamp()
        }), { merge: true });
        return { success: true };
    } catch (error) {
        console.error("Error saving employee settings: ", error);
        throw error;
    }
};

export const subscribeToUserRates = (callback) => {
    const q = wsQuery(collection(db, 'overtime_settings'));
    return onSnapshot(q, (snapshot) => {
        const rates = {};
        snapshot.forEach((doc) => {
            rates[doc.id] = doc.data().hourlyRate;
        });
        callback(rates);
    });
};

export const subscribeToEmployeeSettings = (callback) => {
    const q = wsQuery(collection(db, 'overtime_settings'));
    return onSnapshot(q, (snapshot) => {
        const settings = {};
        snapshot.forEach((d) => {
            settings[d.id] = d.data();
        });
        callback(settings);
    });
};

// ── Attendance Management ──
// Sello del servidor para los marcajes. Se guarda junto a la hora "HH:mm" que
// calcula el navegador para poder detectar y corregir relojes desajustados.
export const serverNow = () => serverTimestamp();

export const addAttendanceRecord = async (record) => {
    try {
        await addDoc(collection(db, 'attendance_records'), withWs({
            ...record,
            createdAt: serverTimestamp()
        }));
        return { success: true };
    } catch (error) {
        console.error("Error adding attendance record: ", error);
        throw error;
    }
};

export const updateAttendanceRecord = async (recordId, updates) => {
    try {
        const { updateDoc: updateDocFn } = await import('firebase/firestore');
        await updateDocFn(doc(db, 'attendance_records', recordId), {
            ...updates,
            updatedAt: serverTimestamp()
        });
        return { success: true };
    } catch (error) {
        console.error("Error updating attendance record: ", error);
        throw error;
    }
};

export const subscribeToAttendanceRecords = (callback) => {
    const q = wsQuery(collection(db, 'attendance_records'), orderBy('date', 'desc'), limit(500));
    return onSnapshot(q, (snapshot) => {
        const records = [];
        snapshot.forEach((d) => {
            records.push({ id: d.id, ...d.data() });
        });
        callback(records);
    }, (error) => {
        console.error('Error subscribing to attendance records:', error);
        callback([]);
    });
};

export const deleteAttendanceRecord = async (recordId) => {
    try {
        await deleteDoc(doc(db, 'attendance_records', recordId));
        return { success: true };
    } catch (error) {
        console.error("Error deleting attendance record: ", error);
        throw error;
    }
};

// Payment Management
export const registerPayment = async (paymentData, file) => {
    try {
        let downloadURL = null;
        if (file) {
            const storageRef = ref(storage, `payment_proofs/${paymentData.userId}/${file.name}_${Date.now()}`);
            await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(storageRef);
        }

        const paymentRef = await addDoc(collection(db, 'overtime_payments'), withWs({
            ...paymentData,
            proofUrl: downloadURL,
            createdAt: serverTimestamp()
        }));
        return { success: true, id: paymentRef.id };
    } catch (error) {
        console.error("Error registering payment: ", error);
        throw error;
    }
};

export const subscribeToPayments = (callback) => {
    const q = wsQuery(collection(db, 'overtime_payments'));
    return onSnapshot(q, (snapshot) => {
        const payments = [];
        snapshot.forEach((doc) => {
            payments.push({ id: doc.id, ...doc.data() });
        });
        callback(payments);
    });
};
